'use client';

import { createContext, useContext } from 'react';
import type { ProfileApi } from '@/lib/api';
import type { ProfileMe, RelationshipRequests } from '@/lib/types';

export interface ProfileUrls {
  groupFinder: string;
  give: string;
  pay: string;
  /** Online Giving's donor sign-in: recurring gifts, payment methods, history live there */
  manageGiving: string;
}

export interface ProfileCtx {
  api: ProfileApi;
  /** The GET /me payload, mutated locally by every optimistic write */
  data: ProfileMe;
  update: (fn: (d: ProfileMe) => ProfileMe) => void;
  /**
   * Run a write in the background after the UI already changed. On failure
   * `revert` puts the old state back and a toast says so; a 401 flips the
   * whole page to signed out. Resolves true when the write landed.
   */
  run: (req: () => Promise<unknown>, revert?: () => void, okMessage?: string) => Promise<boolean>;
  urls: ProfileUrls;
  /** The Pledge_Campaigns row the See Jesus page writes to; its cards wear the campaign's brand */
  seeJesusCampaignId: number;
  /** Mock mode: no MP token exists, so sign-in affordances are no-ops */
  mock: boolean;
  /** Relationship requests, loaded by the Family section, written by anyone who sends one */
  requests: RelationshipRequests | null | 'error';
  setRequests: (next: RelationshipRequests | null | 'error') => void;
  patchRequests: (fn: (r: RelationshipRequests) => RelationshipRequests) => void;
}

export const ProfileContext = createContext<ProfileCtx | null>(null);

export function useProfile(): ProfileCtx {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside ProfileWidget');
  return ctx;
}
